import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, Tooltip, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getDevices, getDeviceHistoryDate, Device, Position } from '../services/api';
import {
  buildArrowData, detectStopEvents, detectParkingEvents,
  makeStopIcon, makeParkingIcon, fmtLocalTime,
} from '../utils/routeAnnotations';
import { parseUtc } from '../utils/dataAge';

/* ── Warstwy mapy ── */
const LAYERS = {
  osm:       { name: '🗺️ Mapa',     url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', maxZoom: 19, attr: '© OpenStreetMap' },
  carto:     { name: '✏️ Czysty',   url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', maxZoom: 19, attr: '© Carto' },
  satellite: { name: '🛰️ Satelita', url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', maxZoom: 23, attr: '© Esri' },
} as const;
type LayerKey = keyof typeof LAYERS;
const ANIM_DELAYS: Record<number, number> = { 1: 1000, 2: 500, 5: 200, 10: 100, 20: 50 };

// Singleton icons
const stopIcon    = makeStopIcon();
const parkingIcon = makeParkingIcon();

/* ── Helpers ── */
const toYesterday = () => {
  const d = new Date(); d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};

const parseAttrs = (raw: any): Record<string, any> => {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw); } catch { return {}; }
};

const filterPositions = (pts: Position[]): Position[] => {
  const valid = pts.filter(p =>
    p.latitude !== 0 && p.longitude !== 0 &&
    !(Math.abs(p.latitude) <= 0.001 && Math.abs(p.longitude) <= 0.001)
  );
  const out: Position[] = [];
  for (const p of valid) {
    if (out.length === 0) { out.push(p); continue; }
    const prev = out[out.length - 1];
    const dLat = (p.latitude  - prev.latitude)  * 111000;
    const dLng = (p.longitude - prev.longitude) * 111000 * Math.cos(p.latitude * Math.PI / 180);
    if (Math.sqrt(dLat * dLat + dLng * dLng) >= 5) out.push(p);
  }
  return out;
};

/* ── Leaflet icons ── */
const makeVehicleIcon = (course: number) => L.divIcon({
  className: '',
  html: `<svg width="22" height="22" viewBox="0 0 22 22" style="transform:rotate(${course}deg);overflow:visible;display:block">
    <circle cx="11" cy="11" r="7" fill="#2d7ef7" stroke="white" stroke-width="2.5"/>
    <polygon points="11,1 7.5,9 14.5,9" fill="#2d7ef7" stroke="white" stroke-width="1.5"/>
  </svg>`,
  iconSize: [22, 22], iconAnchor: [11, 11],
});

const startIcon = L.divIcon({
  className: '',
  html: '<div style="width:13px;height:13px;background:#22c55e;border:2.5px solid #fff;border-radius:50%;box-shadow:0 0 5px rgba(0,0,0,.5)"></div>',
  iconSize: [13, 13], iconAnchor: [6, 6],
});
const endIcon = L.divIcon({
  className: '',
  html: '<div style="width:13px;height:13px;background:#ef4444;border:2.5px solid #fff;border-radius:50%;box-shadow:0 0 5px rgba(0,0,0,.5)"></div>',
  iconSize: [13, 13], iconAnchor: [6, 6],
});

/* ── MapFitter ── */
const MapFitter: React.FC<{ coords: [number, number][] }> = ({ coords }) => {
  const map = useMap();
  useEffect(() => {
    if (coords.length >= 2) map.fitBounds(coords as L.LatLngBoundsExpression, { padding: [40, 40] });
    else if (coords.length === 1) map.setView(coords[0], 15);
  }, [coords, map]);
  return null;
};

/* ── MapController ── */
const MapController: React.FC<{
  lat: number | null; lng: number | null;
  tracking: boolean; playing: boolean; onDrag: () => void;
}> = ({ lat, lng, tracking, playing, onDrag }) => {
  const map = useMap();
  useMapEvents({ dragstart: onDrag });
  useEffect(() => {
    if (tracking && playing && lat !== null && lng !== null) {
      map.setView([lat, lng], map.getZoom(), { animate: true });
    }
  }, [lat, lng, tracking, playing, map]);
  return null;
};

/* ── Main component ── */
const RouteHistory: React.FC = () => {
  const [devices, setDevices]         = useState<Device[]>([]);
  const [deviceId, setDeviceId]       = useState<string>('');
  const [date, setDate]               = useState<string>(toYesterday());
  const [positions, setPositions]     = useState<Position[]>([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [activeLayer, setActiveLayer] = useState<LayerKey>('osm');
  const [currentIdx, setCurrentIdx]   = useState(0);
  const [playing, setPlaying]         = useState(false);
  const [animSpeed, setAnimSpeed]     = useState<number>(5);
  const [tracking, setTracking]       = useState(true);
  const [showStop,    setShowStop]    = useState(false);
  const [showParking, setShowParking] = useState(false);

  const handleDrag = useCallback(() => setTracking(false), []);

  useEffect(() => {
    getDevices().then(d => {
      const list = Array.isArray(d) ? d : [];
      setDevices(list);
      if (list.length > 0) setDeviceId(String(list[0].id));
    }).catch(() => {});
  }, []);

  const coords = useMemo<[number, number][]>(
    () => positions.map(p => [p.latitude, p.longitude]),
    [positions],
  );

  const traveledCoords = useMemo<[number, number][]>(
    () => positions.slice(0, Math.max(2, currentIdx + 1)).map(p => [p.latitude, p.longitude]),
    [positions, currentIdx],
  );

  /* Route annotations — computed from all positions (chronological) */
  const arrowData     = useMemo(() => buildArrowData(positions, 5),    [positions]);
  const stopEvents    = useMemo(() => detectStopEvents(positions),     [positions]);
  const parkingEvents = useMemo(() => detectParkingEvents(positions),  [positions]);

  /* Route summary */
  const routeSummary = useMemo(() => {
    if (positions.length < 2) return null;
    const haversineM = (lat1: number, lon1: number, lat2: number, lon2: number) => {
      const R = 6371000;
      const toRad = (x: number) => x * Math.PI / 180;
      const dLat = toRad(lat2 - lat1);
      const dLon = toRad(lon2 - lon1);
      const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
      return 2 * R * Math.asin(Math.sqrt(a));
    };
    let distanceM = 0, maxSpeedKmh = 0, sumSpeedKmh = 0, countMoving = 0;
    let firstMoving: string | null = null, lastMoving: string | null = null;
    for (let i = 1; i < positions.length; i++) {
      const prev = positions[i - 1], curr = positions[i];
      const d = haversineM(prev.latitude, prev.longitude, curr.latitude, curr.longitude);
      if (d <= 500) distanceM += d;
      const speedKmh = curr.speed_kmh ?? 0;
      if (speedKmh > 0) {
        maxSpeedKmh = Math.max(maxSpeedKmh, speedKmh);
        sumSpeedKmh += speedKmh; countMoving++;
        if (!firstMoving) firstMoving = curr.fixtime;
        lastMoving = curr.fixtime;
      }
    }
    const fmtHM = (t: string) => parseUtc(t).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
    const driveMin = (firstMoving && lastMoving)
      ? Math.round((parseUtc(lastMoving).getTime() - parseUtc(firstMoving).getTime()) / 60000) : 0;
    return {
      distanceKm: (distanceM / 1000).toFixed(1),
      driveTime:  `${String(Math.floor(driveMin / 60)).padStart(2, '0')}:${String(driveMin % 60).padStart(2, '0')}`,
      maxSpeed:   Math.round(maxSpeedKmh),
      avgSpeed:   countMoving > 0 ? Math.round(sumSpeedKmh / countMoving) : 0,
      startTime:  firstMoving ? fmtHM(firstMoving) : '—',
      endTime:    lastMoving  ? fmtHM(lastMoving)  : '—',
      pointCount: positions.length,
    };
  }, [positions]);

  const load = async () => {
    if (!deviceId) return;
    setLoading(true); setError(null); setPlaying(false);
    setCurrentIdx(0); setTracking(true);
    try {
      const raw = await getDeviceHistoryDate(Number(deviceId), date);
      const filtered = filterPositions(raw);
      setPositions(filtered);
      if (filtered.length === 0) setError('Brak danych GPS dla wybranego dnia i pojazdu.');
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Nie udało się pobrać danych.');
      setPositions([]);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!playing || positions.length === 0) return;
    const id = setInterval(() => {
      setCurrentIdx(prev => {
        if (prev >= positions.length - 1) { setPlaying(false); return prev; }
        return prev + 1;
      });
    }, ANIM_DELAYS[animSpeed]);
    return () => clearInterval(id);
  }, [playing, animSpeed, positions.length]);

  const handlePlay = () => {
    if (playing) { setPlaying(false); return; }
    if (currentIdx >= positions.length - 1) setCurrentIdx(0);
    setPlaying(true);
  };

  const currentPos = positions[currentIdx] ?? null;
  const attrs = useMemo(() => parseAttrs(currentPos?.attributes), [currentPos]);

  const currentLatLng = useMemo<[number, number] | null>(
    () => currentPos ? [currentPos.latitude, currentPos.longitude] : null,
    [currentPos],
  );

  const vehicleIcon = useMemo(
    () => currentPos ? makeVehicleIcon(currentPos.course ?? 0) : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentPos?.course],
  );

  const fmtTime = (t: string) => t.replace('Z', '').replace('T', ' ').substring(0, 19);

  const infoItems = currentPos ? [
    { label: 'Czas GPS',  value: fmtTime(currentPos.fixtime) },
    { label: 'Prędkość',  value: `${Math.round(currentPos.speed_kmh ?? 0)} km/h` },
    { label: 'Satelity',  value: attrs.sat  != null ? String(attrs.sat)  : '—' },
    { label: 'GSM',       value: attrs.rssi != null ? String(attrs.rssi) : (attrs.gsm != null ? String(attrs.gsm) : '—') },
    { label: 'Napięcie',  value: attrs.power   != null ? `${Number(attrs.power).toFixed(2)} V`   : (attrs.battery != null ? `${Number(attrs.battery).toFixed(2)} V` : '—') },
    { label: 'Paliwo %',  value: attrs.io89 != null ? `${attrs.io89}%` : '—' },
  ] : [];

  const card: React.CSSProperties = {
    background: '#1a1d23', border: '1px solid #2a2e36', borderRadius: '6px', padding: '12px',
  };
  const labelStyle: React.CSSProperties = {
    fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px',
  };
  const inputStyle: React.CSSProperties = {
    background: '#1a1a1a', color: '#eee', border: '1px solid #2d323c',
    borderRadius: '4px', padding: '5px 8px', fontSize: '13px', fontFamily: 'inherit',
  };

  // Shared style for layer/toggle buttons (white background container)
  const layerBtnBase: React.CSSProperties = {
    border: 'none', background: 'transparent',
    padding: '4px 8px', fontSize: 11,
    cursor: 'pointer', borderRadius: 5, whiteSpace: 'nowrap',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>

      {/* ── Filters ── */}
      <div style={{ ...card, display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={labelStyle}>Data</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} style={inputStyle} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={labelStyle}>Pojazd</label>
          <select value={deviceId} onChange={e => setDeviceId(e.target.value)}
            style={{ ...inputStyle, minWidth: '160px', cursor: 'pointer' }}>
            {devices.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
        <button onClick={load} disabled={loading || !deviceId}
          style={{ background: loading ? '#444' : '#667eea', color: '#fff', border: 'none',
            borderRadius: '4px', padding: '7px 16px', fontSize: '13px', fontWeight: 500,
            cursor: loading || !deviceId ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1 }}>
          {loading ? 'Ładuję…' : 'Załaduj trasę'}
        </button>
      </div>

      {error && (
        <div style={{ background: '#3a1a1a', border: '1px solid #6a2a2a', color: '#ff9a9a',
          padding: '8px 12px', borderRadius: '4px', fontSize: '13px' }}>{error}</div>
      )}

      {/* ── Route summary ── */}
      {routeSummary && (
        <div style={{ ...card, display: 'flex', alignItems: 'stretch', flexWrap: 'wrap' }}>
          {([
            { label: 'Dystans',       value: `${routeSummary.distanceKm} km` },
            { label: 'Czas jazdy',    value: routeSummary.driveTime },
            { label: 'Prędkość max',  value: `${routeSummary.maxSpeed} km/h` },
            { label: 'Prędkość śr.',  value: `${routeSummary.avgSpeed} km/h` },
            { label: 'Start',         value: routeSummary.startTime },
            { label: 'Koniec',        value: routeSummary.endTime },
            { label: 'Punkty GPS',    value: String(routeSummary.pointCount) },
          ] as { label: string; value: string }[]).map(({ label, value }, i, arr) => (
            <div key={label} style={{
              flex: 1, minWidth: 90, textAlign: 'center', padding: '8px 14px',
              borderRight: i < arr.length - 1 ? '1px solid #2a2e36' : 'none',
            }}>
              <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase',
                letterSpacing: '0.5px', marginBottom: 5 }}>{label}</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#ddd',
                fontVariantNumeric: 'tabular-nums' }}>{value}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Map ── */}
      <div style={{ position: 'relative', height: '500px', borderRadius: '8px',
        overflow: 'hidden', border: '1px solid #2a2e36' }}>

        {/* Layer + event toggles */}
        <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 1000, display: 'flex', gap: 4,
          background: 'rgba(255,255,255,0.95)', padding: 4, borderRadius: 8,
          boxShadow: '0 1px 6px rgba(0,0,0,.2)' }}>

          {(Object.keys(LAYERS) as LayerKey[]).map(k => (
            <button key={k} onClick={() => setActiveLayer(k)}
              style={{ ...layerBtnBase,
                background: activeLayer === k ? '#4a6fa5' : 'transparent',
                color:      activeLayer === k ? '#fff'    : '#444' }}>
              {LAYERS[k].name}
            </button>
          ))}

          {/* STOP toggle — road sign B-20 */}
          <button
            onClick={() => setShowStop(v => !v)}
            title="Postoje z pracującym silnikiem"
            style={{ ...layerBtnBase, padding: '2px 5px', lineHeight: 0,
              opacity: showStop ? 1 : 0.4 }}
          >
            <svg width="22" height="22" viewBox="0 0 22 22" xmlns="http://www.w3.org/2000/svg">
              <polygon
                points="8,1 14,1 21,8 21,14 14,21 8,21 1,14 1,8"
                fill="#dc2626" stroke="white" strokeWidth="1.5" strokeLinejoin="round"
              />
              <text x="11" y="13.5" textAnchor="middle" fill="white"
                fontSize="5" fontWeight="bold" fontFamily="Arial,sans-serif">
                STOP
              </text>
            </svg>
          </button>

          {/* Parking toggle — road sign D-18 */}
          <button
            onClick={() => setShowParking(v => !v)}
            title="Postoje (wyłączony silnik)"
            style={{ ...layerBtnBase, padding: '2px 5px', lineHeight: 0,
              opacity: showParking ? 1 : 0.4 }}
          >
            <svg width="22" height="22" viewBox="0 0 22 22" xmlns="http://www.w3.org/2000/svg">
              <rect x="1" y="1" width="20" height="20" rx="3"
                fill="#2b6cc4" stroke="white" strokeWidth="1.5" />
              <text x="11" y="16" textAnchor="middle" fill="white"
                fontSize="13" fontWeight="bold" fontFamily="Arial,sans-serif">
                P
              </text>
            </svg>
          </button>
        </div>

        <MapContainer center={[51.9, 19.1]} zoom={6} maxZoom={23}
          style={{ height: '100%', width: '100%' }}>
          <TileLayer key={activeLayer} url={LAYERS[activeLayer].url}
            attribution={LAYERS[activeLayer].attr} maxZoom={LAYERS[activeLayer].maxZoom} />

          <MapFitter coords={coords} />
          <MapController
            lat={currentLatLng?.[0] ?? null} lng={currentLatLng?.[1] ?? null}
            tracking={tracking} playing={playing} onDrag={handleDrag}
          />

          {/* Full route (grey background) */}
          {coords.length >= 2 && (
            <Polyline positions={coords} color="#777777" weight={3} opacity={0.5} />
          )}

          {/* Traveled portion (grows during animation) */}
          {traveledCoords.length >= 2 && (
            <Polyline positions={traveledCoords} color="#667eea" weight={4} opacity={0.9} />
          )}

          {/* Direction arrows — always visible */}
          {arrowData.map((arrow, i) => (
            <Marker key={`arr-${i}`} position={arrow.position} icon={arrow.icon}>
              <Tooltip direction="top" offset={[0, -10]}>
                <div style={{ fontSize: 12, lineHeight: '1.7', whiteSpace: 'nowrap' }}>
                  <b>Prędkość:</b> {arrow.speedKmh} km/h<br />
                  {arrow.rpm != null && (
                    <><b>Obroty:</b> {arrow.rpm} rpm<br /></>
                  )}
                  <b>Czas:</b> {fmtLocalTime(arrow.fixtime)}
                </div>
              </Tooltip>
            </Marker>
          ))}

          {/* STOP events */}
          {showStop && stopEvents.map((ev, i) => (
            <Marker key={`stop-${i}`} position={ev.position} icon={stopIcon}>
              <Tooltip direction="top" offset={[0, -16]}>
                <div style={{ fontSize: 12, lineHeight: '1.7', whiteSpace: 'nowrap' }}>
                  <b>Postój silnik pracuje</b><br />
                  Początek: {fmtLocalTime(ev.startTime)}<br />
                  Czas trwania: {ev.durationMin} min
                </div>
              </Tooltip>
            </Marker>
          ))}

          {/* Parking events */}
          {showParking && parkingEvents.map((ev, i) => (
            <Marker key={`park-${i}`} position={ev.position} icon={parkingIcon}>
              <Tooltip direction="top" offset={[0, -16]}>
                <div style={{ fontSize: 12, lineHeight: '1.7', whiteSpace: 'nowrap' }}>
                  <b>Postój wyłączony silnik</b><br />
                  Początek: {fmtLocalTime(ev.startTime)}<br />
                  Czas trwania: {ev.durationMin} min
                </div>
              </Tooltip>
            </Marker>
          ))}

          {/* Start / End markers */}
          {positions.length > 0 && (
            <Marker position={[positions[0].latitude, positions[0].longitude]} icon={startIcon} />
          )}
          {positions.length > 1 && (
            <Marker
              position={[positions[positions.length - 1].latitude, positions[positions.length - 1].longitude]}
              icon={endIcon}
            />
          )}

          {/* Animated vehicle */}
          {currentPos && vehicleIcon && (
            <Marker position={[currentPos.latitude, currentPos.longitude]} icon={vehicleIcon} />
          )}
        </MapContainer>
      </div>

      {/* ── Animation controls ── */}
      {positions.length > 0 && (
        <div style={card}>
          <div style={{ fontSize: '12px', color: '#aaa', marginBottom: '8px', textAlign: 'center' }}>
            {currentPos && (
              <>
                🕐 <strong style={{ color: '#ddd' }}>{fmtTime(currentPos.fixtime)}</strong>
                <span style={{ marginLeft: 12, color: '#666' }}>
                  punkt {currentIdx + 1} / {positions.length}
                </span>
              </>
            )}
          </div>
          <input type="range" min={0} max={positions.length - 1} value={currentIdx}
            onChange={e => { setPlaying(false); setCurrentIdx(Number(e.target.value)); }}
            style={{ width: '100%', marginBottom: '10px', accentColor: '#667eea' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={() => { setPlaying(false); setCurrentIdx(0); }} title="Reset"
              style={{ background: '#2a2d35', color: '#ccc', border: '1px solid #3a3f4c',
                borderRadius: 4, padding: '5px 12px', fontSize: 18, cursor: 'pointer' }}>⏮</button>
            <button onClick={handlePlay}
              style={{ background: playing ? '#e05555' : '#667eea', color: '#fff', border: 'none',
                borderRadius: 4, padding: '5px 18px', fontSize: 18, cursor: 'pointer', minWidth: 56 }}>
              {playing ? '⏸' : '▶'}
            </button>
            <label style={{ display: 'flex', alignItems: 'center', gap: 5,
              fontSize: 12, color: tracking ? '#aaa' : '#555', cursor: 'pointer', userSelect: 'none' }}>
              <input type="checkbox" checked={tracking} onChange={e => setTracking(e.target.checked)}
                style={{ accentColor: '#667eea', cursor: 'pointer' }} />
              Śledź pojazd
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
              <span style={{ fontSize: 12, color: '#888' }}>Prędkość:</span>
              {[1, 2, 5, 10, 20].map(s => (
                <button key={s} onClick={() => setAnimSpeed(s)}
                  style={{
                    background: animSpeed === s ? '#667eea' : '#2a2d35',
                    color:      animSpeed === s ? '#fff'    : '#aaa',
                    border:     `1px solid ${animSpeed === s ? '#667eea' : '#3a3f4c'}`,
                    borderRadius: 4, padding: '3px 8px', fontSize: 12, cursor: 'pointer',
                  }}>{s}x</button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Info panel ── */}
      {currentPos && infoItems.length > 0 && (
        <div style={card}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px 24px' }}>
            {infoItems.map(({ label, value }) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between',
                fontSize: 12, borderBottom: '1px solid #20242c', paddingBottom: 4 }}>
                <span style={{ color: '#888' }}>{label}</span>
                <span style={{ color: '#ddd', fontVariantNumeric: 'tabular-nums' }}>{String(value)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RouteHistory;
